'use client';

import { c as _c } from "react/compiler-runtime";
import type { MarketId } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { useCachedValue, ZERO } from '@morpho-org/dapp-core';
import { formatLongString, isDefined, type Time } from '@morpho-org/morpho-ts';
import { type OptionSelect, Select } from '@morpho-org/ui-v6/client';
import { Card, CardText, Skeleton, TableNoResults } from '@morpho-org/ui-v6/server';
import { formatters, getDigitsFromPriceAndAmount } from '@morpho-org/ui-v6/utils';
import { SegmentedControlItem, SegmentedControlList, Tabs, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { parseEther } from 'viem';
import type { MarketPositionMarket, PositionToken } from '@/lib/procedures/marketPositionMarket.contract';
import SimpleChart from '@/components/charts/components/SimpleChart';
import { formatTimeseriesData, isSerieEmpty, removeNullYValuesFromSeries } from '@/components/charts/helpers/highcharts';
import { useHighchartsOptions } from '@/components/charts/hooks/useHighchartsOptions';
import { defaultTimeFrame, timeFrameOptions } from '@/config';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getParsedValue } from '@/helpers/formatters';
import { splitValueAndUnit } from '@/helpers/number';
import { useIsMobile } from '@/hooks/useBreakpoints';
import { orpc } from '@/lib/orpc/client';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { getHasBorrowPosition, getHasLendPosition } from '../helpers/position';
import { ChartOverviewMyBorrowError, ChartOverviewMyBorrowLoading } from './states';
import { toHistoryRange, toPositionTotals } from './totals';
export interface ChartOverviewMyBorrowClientProps {
  chainId: number;
  marketId: MarketId;
  market: MarketPositionMarket;
}
type Mode = 'collateral' | 'borrowAssets';
type Currency = 'asset' | 'USD';
const modeOptions: OptionSelect<Mode>[] = [{
  label: 'Loan',
  value: 'borrowAssets'
}, {
  label: 'Collateral',
  value: 'collateral'
}];

/** The history field each mode charts, natively and in USD. */
const SERIES = {
  collateral: {
    assets: 'collateralAssets',
    usd: 'collateralUsd'
  },
  borrowAssets: {
    assets: 'borrowAssets',
    usd: 'borrowAssetsUsd'
  }
} as const satisfies Record<Mode, Record<'assets' | 'usd', string>>;
const baseTitles: Record<Mode, string> = {
  collateral: 'Your Collateral',
  borrowAssets: 'Your Borrow Position'
};
const formatter = formatters.short.default('0.00');

/** The token's USD price as a WAD, or undefined when Core cannot price it. */
const toPriceWad = (token: PositionToken | null) => !token || token.priceUsd === null ? undefined : parseEther(token.priceUsd.toFixed(18));
const ChartOverviewMyBorrowClient = t0 => {
  const $ = _c(87);
  const {
    chainId,
    marketId,
    market
  } = t0;
  const {
    palette
  } = useHighchartsOptions();
  const isMobile = useIsMobile();
  const {
    positionHistory: isPositionHistoryEnabled,
    marketLendPosition: isMarketLendPositionEnabled
  } = useFeatureFlags();
  const {
    account
  } = useWeb3Context();
  const userAddress = account.address;
  const [timeFrame, setTimeFrame] = useState(defaultTimeFrame);
  const [mode, setMode] = useState("borrowAssets");
  const [currency, setCurrency] = useState("USD");
  let t1;
  if ($[0] !== chainId || $[1] !== marketId || $[2] !== userAddress) {
    t1 = orpc.market.position.queryOptions({
      input: userAddress ? {
        chainId,
        marketId,
        userAddress
      } : skipToken
    });
    $[0] = chainId;
    $[1] = marketId;
    $[2] = userAddress;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const positionQuery = useQuery(t1);
  let t2;
  if ($[4] !== chainId || $[5] !== isPositionHistoryEnabled || $[6] !== marketId || $[7] !== timeFrame || $[8] !== userAddress) {
    t2 = orpc.market.positionHistory.queryOptions({
      input: userAddress && isPositionHistoryEnabled ? {
        chainId,
        marketId,
        userAddress,
        range: toHistoryRange(timeFrame)
      } : skipToken
    });
    $[4] = chainId;
    $[5] = isPositionHistoryEnabled;
    $[6] = marketId;
    $[7] = timeFrame;
    $[8] = userAddress;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const historyQuery = useQuery(t2);
  const position = userAddress ? positionQuery.data : undefined;
  let t3;
  if ($[10] !== market || $[11] !== position) {
    t3 = position ? toPositionTotals(position, market) : undefined;
    $[10] = market;
    $[11] = position;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const totals = t3;
  const token = mode === "collateral" ? market.collateralToken : market.loanToken;
  const isUsd = currency === "USD" || !token?.symbol;
  const decimals = token?.decimals ?? 18;
  let t4;
  if ($[13] !== token) {
    t4 = token?.symbol ? [{
      value: "asset",
      label: token.symbol
    }, {
      value: "USD",
      label: "USD"
    }] : [{
      value: "USD",
      label: "USD"
    }];
    $[13] = token;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const currencyOptions = t4;
  const points = historyQuery.data?.points;
  const shownPoints = useCachedValue(points);
  let formattedData;
  let t5;
  if ($[15] !== decimals || $[16] !== isUsd || $[17] !== mode || $[18] !== points) {
    formattedData = removeNullYValuesFromSeries(isUsd ? formatTimeseriesData(points?.map(point => ({
      x: point.timestamp,
      y: point[SERIES[mode].usd]
    }))) : formatTimeseriesData(points?.map(point_0 => ({
      x: point_0.timestamp,
      y: point_0[SERIES[mode].assets]
    })), decimals));
    t5 = isSerieEmpty(formattedData);
    $[15] = decimals;
    $[16] = isUsd;
    $[17] = mode;
    $[18] = points;
    $[19] = formattedData;
    $[20] = t5;
  } else {
    formattedData = $[19];
    t5 = $[20];
  }
  const hasData = !t5;
  let t6;
  if ($[21] !== isUsd || $[22] !== mode || $[23] !== points) {
    t6 = isUsd && !!points?.length && points.every(point_1 => point_1[SERIES[mode].usd] === null);
    $[21] = isUsd;
    $[22] = mode;
    $[23] = points;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const isUnpricedUsdSeries = t6;
  let t7;
  if ($[25] !== position) {
    t7 = isDefined(position);
    $[25] = position;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const hasResolvedPosition = t7;
  const isZeroPosition = !!position && BigInt(position.collateralAssets) === ZERO && BigInt(position.borrowShares) === ZERO && BigInt(position.supplyShares) === ZERO;
  const showEmptyState = isPositionHistoryEnabled && !historyQuery.isError && isZeroPosition && points?.length === 0 && !hasData;
  const totalBorrowAssets = useCachedValue(totals?.borrowAssets);
  const totalCollateral = useCachedValue(totals?.collateral);
  const totalBorrowAssetsUsd = useCachedValue(totals?.borrowAssetsUsd);
  const totalCollateralUsd = useCachedValue(totals?.collateralUsd);
  let displayedTitle;
  let displayedTotalValue;
  let t8;
  let total;
  let unit;
  if ($[27] !== decimals || $[28] !== isUsd || $[29] !== mode || $[30] !== token || $[31] !== totalBorrowAssets || $[32] !== totalBorrowAssetsUsd || $[33] !== totalCollateral || $[34] !== totalCollateralUsd || $[35] !== totals?.borrowAssetsUsd || $[36] !== totals?.collateralUsd) {
    let t9;
    bb0: {
      const amount = mode === "collateral" ? totalCollateral : totalBorrowAssets;
      const usd = mode === "collateral" ? totalCollateralUsd : totalBorrowAssetsUsd;
      const liveUsd = mode === "collateral" ? totals?.collateralUsd : totals?.borrowAssetsUsd;
      if (isUsd) {
        t9 = {
          value: getParsedValue(usd, safeParseNumber),
          decimals: 18,
          isUnpriced: liveUsd === null
        };
        break bb0;
      }
      t9 = {
        value: amount === undefined ? null : BigInt(amount),
        decimals,
        isUnpriced: false
      };
    }
    total = t9;
    const digits = isUsd ? 2 : getDigitsFromPriceAndAmount(toPriceWad(token), total.value, token?.decimals);
    displayedTotalValue = splitValueAndUnit(formatter.digits(digits).trailingZero(!total.value).of(total.value, total.decimals));
    const symbol = token?.symbol;
    let t10;
    if ($[42] !== mode || $[43] !== symbol) {
      t10 = symbol ? `${baseTitles[mode]} (${formatLongString(symbol, 15)})` : baseTitles[mode];
      $[42] = mode;
      $[43] = symbol;
      $[44] = t10;
    } else {
      t10 = $[44];
    }
    displayedTitle = t10;
    unit = formatLongString((isUsd ? "$" : symbol) ?? "", 15);
    t8 = formatters.commas.unit(unit).digits(digits).trailingZero(!total.value).of(total.value, total.decimals);
    $[27] = decimals;
    $[28] = isUsd;
    $[29] = mode;
    $[30] = token;
    $[31] = totalBorrowAssets;
    $[32] = totalBorrowAssetsUsd;
    $[33] = totalCollateral;
    $[34] = totalCollateralUsd;
    $[35] = totals?.borrowAssetsUsd;
    $[36] = totals?.collateralUsd;
    $[37] = displayedTitle;
    $[38] = displayedTotalValue;
    $[39] = t8;
    $[40] = total;
    $[41] = unit;
  } else {
    displayedTitle = $[37];
    displayedTotalValue = $[38];
    t8 = $[39];
    total = $[40];
    unit = $[41];
  }
  const detailedFormattedTotal = t8;
  if (userAddress && positionQuery.isError && positionQuery.data === undefined) {
    let t9;
    if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <ChartOverviewMyBorrowError />;
      $[45] = t9;
    } else {
      t9 = $[45];
    }
    return t9;
  }
  const positionState = position && {
    supplyShares: position.supplyShares,
    supplyAssetsUsd: totals?.supplyAssetsUsd,
    borrowShares: position.borrowShares,
    collateral: position.collateralAssets
  };
  if (isMarketLendPositionEnabled && getHasLendPosition(positionState) && !getHasBorrowPosition(positionState)) {
    return null;
  }
  if (isPositionHistoryEnabled && (positionQuery.isLoading && !hasResolvedPosition && totalBorrowAssets === undefined || historyQuery.isLoading && shownPoints === undefined)) {
    let t9;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <ChartOverviewMyBorrowLoading />;
      $[46] = t9;
    } else {
      t9 = $[46];
    }
    return t9;
  }
  if (showEmptyState) {
    let t9;
    if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div data-testid="market-my-borrow-chart-empty"><TableNoResults text="No position found." height="400px" withBorder={true} className="rounded-[20px]" /></div>;
      $[47] = t9;
    } else {
      t9 = $[47];
    }
    return t9;
  }
  let t9;
  if ($[48] !== displayedTitle) {
    t9 = <span className="text-secondary-foreground text-xs">{displayedTitle}</span>;
    $[48] = displayedTitle;
    $[49] = t9;
  } else {
    t9 = $[49];
  }
  let t10;
  if ($[50] !== detailedFormattedTotal || $[51] !== displayedTotalValue || $[52] !== isUsd || $[53] !== positionQuery.isLoading || $[54] !== total) {
    t10 = positionQuery.isLoading && total.value === null ? <div className="gap-sm flex items-center"><Skeleton className="h-(--text-title-sm--line-height) w-[130px] rounded-[20px]" /><Skeleton className="h-[26px] w-[50px] rounded-[20px]" />{" "}</div> : total.isUnpriced ? <span className="text-title-sm" data-testid="market-my-borrow-total">-</span> : <div className="gap-sm flex items-center"><Tooltip><TooltipTrigger asChild={true}><div className="flex cursor-default items-center gap-[2px]" data-testid="market-my-borrow-total">{isUsd && <span className="text-title-sm text-muted-foreground">$</span>}<span className="text-title-sm">{displayedTotalValue.value}</span><span className="text-title-sm text-muted-foreground">{displayedTotalValue.unit}</span></div></TooltipTrigger>{!!detailedFormattedTotal && <TooltipContent side="right">{detailedFormattedTotal}</TooltipContent>}</Tooltip></div>;
    $[50] = detailedFormattedTotal;
    $[51] = displayedTotalValue;
    $[52] = isUsd;
    $[53] = positionQuery.isLoading;
    $[54] = total;
    $[55] = t10;
  } else {
    t10 = $[55];
  }
  let t11;
  if ($[56] !== t10 || $[57] !== t9) {
    t11 = <div className="gap-xs flex flex-col">{t9}{t10}</div>;
    $[56] = t10;
    $[57] = t9;
    $[58] = t11;
  } else {
    t11 = $[58];
  }
  let t12;
  if ($[59] !== currencyOptions || $[60] !== hasResolvedPosition || $[61] !== isMobile || $[62] !== isUsd || $[63] !== mode) {
    t12 = isMobile ? <><Select options={modeOptions} value={mode} onChange={setMode} disabled={!hasResolvedPosition} /><Select options={currencyOptions} value={isUsd ? "USD" : "asset"} onChange={setCurrency} disabled={!hasResolvedPosition} /></> : <><Tabs value={mode} onValueChange={value => setMode(value as Mode)}><SegmentedControlList size="default" aria-label="Position">{modeOptions.map(option => <SegmentedControlItem key={option.value} value={option.value} disabled={!hasResolvedPosition}>{option.label}</SegmentedControlItem>)}</SegmentedControlList></Tabs><Tabs value={isUsd ? "USD" : "asset"} onValueChange={value_0 => setCurrency(value_0 as Currency)}><SegmentedControlList size="default" aria-label="Currency">{currencyOptions.map(option_0 => <SegmentedControlItem key={option_0.value} value={option_0.value} disabled={!hasResolvedPosition}>{option_0.label}</SegmentedControlItem>)}</SegmentedControlList></Tabs></>;
    $[59] = currencyOptions;
    $[60] = hasResolvedPosition;
    $[61] = isMobile;
    $[62] = isUsd;
    $[63] = mode;
    $[64] = t12;
  } else {
    t12 = $[64];
  }
  const t13 = !hasResolvedPosition || !isPositionHistoryEnabled;
  let t14;
  if ($[65] !== t13 || $[66] !== timeFrame) {
    t14 = <Select options={timeFrameOptions} value={timeFrame} onChange={setTimeFrame} disabled={t13} />;
    $[65] = t13;
    $[66] = timeFrame;
    $[67] = t14;
  } else {
    t14 = $[67];
  }
  let t15;
  if ($[68] !== t12 || $[69] !== t14) {
    t15 = <div className="gap-sm flex flex-wrap items-center">{t12}{t14}</div>;
    $[68] = t12;
    $[69] = t14;
    $[70] = t15;
  } else {
    t15 = $[70];
  }
  let t16;
  if ($[71] !== t11 || $[72] !== t15) {
    t16 = <div className="gap-sm flex flex-wrap items-center justify-between">{t11}{t15}</div>;
    $[71] = t11;
    $[72] = t15;
    $[73] = t16;
  } else {
    t16 = $[73];
  }
  let t17;
  if ($[74] !== formattedData || $[75] !== hasData || $[76] !== historyQuery.isError || $[77] !== historyQuery.isLoading || $[78] !== isPositionHistoryEnabled || $[79] !== isUnpricedUsdSeries || $[80] !== mode || $[81] !== palette || $[82] !== unit) {
    t17 = !isPositionHistoryEnabled ? <CardText text="Position History is temporarily unavailable." height="224px" /> : <SimpleChart statuses={{
      loading: historyQuery.isLoading,
      error: historyQuery.isError || isUnpricedUsdSeries
    }} unit={unit} height="224px" series={hasData ? [{
      type: "areaspline",
      name: `Total ${mode}`,
      data: formattedData,
      color: palette.colors[0],
      lineColor: palette.colors[0],
      fillColor: palette.areaChartColors[0]
    }] : []} additionalOptions={{
      chart: {
        plotBackgroundColor: "var(--muted)"
      }
    }} />;
    $[74] = formattedData;
    $[75] = hasData;
    $[76] = historyQuery.isError;
    $[77] = historyQuery.isLoading;
    $[78] = isPositionHistoryEnabled;
    $[79] = isUnpricedUsdSeries;
    $[80] = mode;
    $[81] = palette;
    $[82] = unit;
    $[83] = t17;
  } else {
    t17 = $[83];
  }
  let t18;
  if ($[84] !== t16 || $[85] !== t17) {
    t18 = <Card className="gap-lg py-lg px-md md:px-lg w-[100%] flex-col" withBorder={true} data-testid="market-my-borrow-chart">{t16}{t17}</Card>;
    $[84] = t16;
    $[85] = t17;
    $[86] = t18;
  } else {
    t18 = $[86];
  }
  return t18;
};
export default ChartOverviewMyBorrowClient;